import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Boxes,
  ScanLine,
  ArrowUpFromLine,
  ArrowRightLeft,
  History,
  Grid3x3,
  ClipboardCheck,
  Building2,
  Users as UsersIcon,
  LogOut,
  Menu,
  X,
  Warehouse,
  FileSpreadsheet,
  Cuboid,
  Wrench,
  Sun,
  ListTodo,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import useAutoRefresh from "../lib/useAutoRefresh";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Kontrol Paneli", end: true, testid: "nav-dashboard" },
  { to: "/stok", icon: Boxes, label: "Stok Listesi", testid: "nav-inventory" },
  { to: "/toplu-giris", icon: ScanLine, label: "Toplu Giriş", testid: "nav-bulk-entry" },
  { to: "/toplu-cikis", icon: ArrowUpFromLine, label: "Toplu Çıkış", testid: "nav-bulk-exit" },
  { to: "/transfer", icon: ArrowRightLeft, label: "Sehpa Transferi", testid: "nav-transfer" },
  { to: "/hareketler", icon: History, label: "Hareket Geçmişi", testid: "nav-movements" },
  { to: "/sehpalar", icon: Grid3x3, label: "Sehpalar", testid: "nav-shelves" },
  { to: "/depo-3d", icon: Cuboid, label: "3D Depo", testid: "nav-warehouse-3d" },
  { to: "/makina-parcalari", icon: Wrench, label: "Makina Parçaları", testid: "nav-machine-parts" },
  { to: "/excel-aktarim", icon: FileSpreadsheet, label: "Excel Aktarımı", testid: "nav-excel-transfer" },
  { to: "/gorevler", icon: ListTodo, label: "Görevler", testid: "nav-tasks" },
  { to: "/silme-talepleri", icon: ClipboardCheck, label: "Onay Bekleyen", testid: "nav-deletion-requests", roles: ["supervisor"] },
  { to: "/firmalar", icon: Building2, label: "Firmalar", testid: "nav-brands", roles: ["admin", "supervisor"] },
  { to: "/kullanicilar", icon: UsersIcon, label: "Kullanıcılar", testid: "nav-users", roles: ["supervisor"] },
];

const ROLE_LABEL = { supervisor: "SUPERVISOR", admin: "YÖNETİCİ", user: "OPERATÖR" };

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const isSupervisor = user?.role === "supervisor";

  const loadPending = () => {
    if (!isSupervisor) return;
    api.get("/approvals/pending-count").then(({ data }) => setPendingCount(data.count)).catch(() => {});
  };

  useEffect(() => {
    loadPending();
  }, [location.pathname, isSupervisor]);

  useAutoRefresh(loadPending, 30 * 1000);

  const currentTitle =
    NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to) && n.to !== "/"))?.label ||
    "Kontrol Paneli";

  return (
    <div className="min-h-screen flex bg-[color:var(--bg)]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[color:var(--surface)] border-r border-[color:var(--border)] flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-20 flex items-center gap-3 px-4 border-b border-[color:var(--border)] bg-white">
          <img
            src="/brand/stargrup-logo.png"
            alt="StarGrup"
            className="h-12 w-auto"
          />
          <div>
            <div className="font-black text-sm leading-tight">CAM STOK</div>
            <div className="ticker text-[10px] text-[color:var(--text-3)]">
              Adresleme Defteri
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 space-y-1 px-2">
          {NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 hard text-sm transition-colors ${
                    isActive
                      ? "bg-[color:var(--primary)] text-white font-semibold"
                      : "text-[color:var(--text-2)] hover:bg-[color:var(--bg)] hover:text-[color:var(--text)]"
                  }`
                }
              >
                <Icon size={18} strokeWidth={2} />
                <span>{n.label}</span>
                {n.testid === "nav-deletion-requests" && pendingCount > 0 && (
                  <span
                    data-testid="nav-pending-badge"
                    className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-[color:var(--error)] text-white text-[11px] font-bold flex items-center justify-center"
                  >
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-[color:var(--border)]">
          <div className="flex items-center gap-3 p-2">
            <div className="w-9 h-9 bg-[color:var(--primary)] text-white flex items-center justify-center hard font-bold uppercase text-sm">
              {(user?.name || user?.email || "?").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">
                {user?.name || user?.email}
              </div>
              <div className="ticker text-[10px] text-[color:var(--text-3)]" data-testid="sidebar-user-role">
                {ROLE_LABEL[user?.role] || "OPERATÖR"}
              </div>
            </div>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="p-2 hover:bg-[color:var(--bg)] hard text-[color:var(--text-2)] hover:text-[color:var(--error)] transition-colors"
              title="Çıkış Yap"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-[color:var(--surface)] border-b border-[color:var(--border)] flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 hard hover:bg-[color:var(--bg)]"
              onClick={() => setOpen((s) => !s)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div>
              <div className="ticker text-[10px] text-[color:var(--text-3)]">
                /// {new Date().toLocaleDateString("tr-TR", { weekday: "long" }).toUpperCase()}
              </div>
              <h1 className="text-lg font-black tracking-tight">{currentTitle}</h1>
            </div>
          </div>
          <div className="ticker text-[10px] text-[color:var(--text-3)] hidden sm:block">
            {new Date().toLocaleDateString("tr-TR", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
